import type {
  ComponentPropsWithoutRef,
  FunctionComponent,
  HTMLProps,
} from 'react'
import React from 'react'

import classnames from 'classnames'

import type { ImageSource, ValidTypographyTags } from '../../../types'
import { AnimationWrapper } from '../../AnimationWrapper'
import { BaseLink } from '../../Links'
import type { AspectRatio } from '../../Picture'
import { Picture } from '../../Picture'
import { TypographyV2 } from '../../Seatbelt2/Foundations/Typography'
import styles from './PromoCard.module.scss'

export const PromoCard: FunctionComponent<PromoCardProps> = ({
  className = '',
  href = '',
  image = { src: '', alt: '' },
  imageAspectRatio = 'one-to-one',
  strapline = '',
  title = '',
  titleTag = 'h2',
  gtmProps,
  ...rest
}) => (
  <article
    data-testid="promo-card"
    className={classnames(styles.card, className)}
    {...rest}
  >
    <BaseLink href={href} invertedUnderline={true} {...gtmProps}>
      <Picture
        className={imageAspectRatio === 'one-to-one' ? styles.squareImage : ''}
        aspectRatioMain={imageAspectRatio}
        src={image.src}
        sources={image.sources}
        alt={image.alt}
      />
      <div className={styles.content}>
        {title && (
          <TypographyV2
            tag={titleTag}
            textStyle="sb-text-interface-heading-large"
            className={classnames(strapline && styles.strapline)}
          >
            <AnimationWrapper inverted={true}>{title}</AnimationWrapper>
          </TypographyV2>
        )}
        {strapline && (
          <TypographyV2 textStyle="sb-text-interface-body-default-regular">
            {strapline}
          </TypographyV2>
        )}
      </div>
    </BaseLink>
  </article>
)

///////// IMPLEMENTATION /////////

export type PromoCardProps = {
  className?: string
  gtmProps?: {
    'data-which-id'?: string
    'data-section'?: string
  } & HTMLProps<HTMLAnchorElement>
  href: string
  image: {
    alt: string
    sources?: ImageSource[]
    src: string
  }
  imageAspectRatio?: PromoImgAR
  strapline?: string
  title?: string
  titleTag?: ValidTypographyTags
} & ComponentPropsWithoutRef<'article'>

export type PromoImgAR = Extract<AspectRatio, 'one-to-one' | 'two-to-one'>
